import React from "react";
import { Disc3, Grid3x3, AudioWaveform, ListMusic, Play, Sparkles, Compass } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STEPS = [
  {
    icon: Grid3x3,
    color: "#ffb800",
    title: "1 · Make a beat",
    body: "Open the Beats & Keys tab, tap a preset (House, Trap…), and press the green Play button at the top. Tap the grid squares to make it your own.",
  },
  {
    icon: Disc3,
    color: "#00e5ff",
    title: "2 · Play instruments",
    body: "The piano keyboard plays a Grand Piano by default — use your mouse or computer keys (A S D F…). Pick other sounds from the Sound menu.",
  },
  {
    icon: AudioWaveform,
    color: "#a855f7",
    title: "3 · Record & layer",
    body: "In the Timeline tab, record your mic or import audio. Add and name tracks (Vocals, Bass…) — each gets its own volume and effects.",
  },
  {
    icon: ListMusic,
    color: "#00ff66",
    title: "4 · Mix, play & export",
    body: "Use the Mixing Console at the bottom to balance everything, shape sounds in the Effects Rack, then Export your song as a WAV. The Player tab plays your music like a playlist.",
  },
];

export default function OnboardingGuide({ open, onClose, onDemo, autostart, onToggleAutostart, onManual }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#111] border-[#262626] text-white max-w-lg"
        data-testid="onboarding-guide"
      >
        <DialogHeader>
          <DialogTitle className="sr-only">Welcome to BeatMXR</DialogTitle>
          <img src="/wordmark_stacked.png" alt="BeatMXR" className="w-56 mx-auto object-contain" />
        </DialogHeader>
        <p className="text-sm text-neutral-400 text-center mt-1">
          Your all-in-one music studio. No experience needed — here's the 60-second tour.
        </p>

        <div className="flex flex-col gap-3 mt-2">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex gap-3 items-start">
                <div
                  className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: `${s.color}1a`, border: `1px solid ${s.color}55` }}
                >
                  <Icon size={17} style={{ color: s.color }} />
                </div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: s.color }}>
                    {s.title}
                  </div>
                  <div className="text-xs text-neutral-400 leading-relaxed">{s.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button
            data-testid="demo-beat-btn"
            onClick={onDemo}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-cyan-500 text-black font-bold hover:bg-cyan-400 transition-colors"
          >
            <Play size={16} fill="black" /> Play me a demo beat
          </button>
          <button
            data-testid="explore-btn"
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm border border-[#404040] hover:bg-[#242424] transition-colors font-semibold"
          >
            <Compass size={16} /> I'll explore myself
          </button>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#222]">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="autostart-label">
            <input
              type="checkbox"
              data-testid="autostart-checkbox"
              checked={!!autostart}
              onChange={(e) => onToggleAutostart(e.target.checked)}
              className="w-4 h-4 accent-cyan-500 cursor-pointer"
            />
            <span className="text-xs text-neutral-400">Show this guide on startup</span>
          </label>
          <button
            data-testid="open-manual-btn"
            onClick={onManual}
            className="text-xs text-[#00e5ff] hover:underline"
          >
            Open full manual →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
